/**
 * OpenFoodFacts boundary adapter for barcode lookup parsing and transformation.
 *
 * This module maps upstream OFF payloads into core branded-food create inputs.
 * It is best-effort: invalid or incomplete payloads resolve to typed failure
 * reasons instead of throwing business-domain errors.
 *
 * @see `docs/data-imports/openfoodfacts.md`
 */
import { Unit } from "@corpuscomplete/core/common/unit";
import { Barcode } from "@corpuscomplete/core/common/barcode";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { capitalizeFirst, createId, must } from "@corpuscomplete/core/utils";
import { z } from "zod/v4";
import type { Food } from "../../food";
import type { BrandedFood } from "../../branded-food";
import type { ServingParser } from "../../food-import";
import type { CategoryDecisionTrace } from "../../food-category/category-decision";
import { FoodCategoryCrosswalkPolicy } from "../../food-category/policy";
import { evaluateFoodCategoryPolicyDetails } from "../../food-category/policy-evaluator";
import {
  normalizedSourceIdMap,
  normalizeOffCategorySourceId,
} from "../../food-category/source-id-normalization";
import { OpenFoodFactsTaxonomy } from "../openfoodfacts-taxonomy";

const OFF_UPSTREAM_BASE_URL = "https://world.openfoodfacts.org";
const OFF_USER_AGENT = "CorpusComplete/1.0 (+https://corpuscomplete.com)";
const OFF_LOOKUP_TIMEOUT_MS = 10_000;
const OFF_LOOKUP_FIELDS = [
  "_id",
  "code",
  "rev",
  "brands",
  "product_name_en",
  "product_name",
  "product_name_fr",
  "countries_tags",
  "categories_tags",
  "serving_size",
  "serving_quantity",
  "serving_quantity_unit",
  "nutriments",
  "data_quality_warnings_tags",
  "data_quality_bugs_tags",
  "data_quality_errors_tags",
  "quantity",
  "product_quantity",
  "product_quantity_unit",
].join(",");

namespace OpenFoodFactsImporter {
  type BrandAndProductName = {
    brandName: string;
    productName: string;
  };

  const dePrioritizeBrandSubstrings = ["Co.", "Company", "Corporation", "Inc."];
  const lowercaseWords = new Set([
    "of",
    "and",
    "or",
    "the",
    "an",
    "in",
    "on",
    "at",
    "by",
    "to",
    "for",
    "with",
    "from",
  ]);

  const toTitleCase = (text: string): string => {
    return text
      .split(/[\s-]+/)
      .map((word, index) => {
        if (word.length === 0) return word;
        if (index === 0 || !lowercaseWords.has(word.toLowerCase())) {
          // First word or not in lowercase list - apply title case
          const isUniformCase = word === word.toUpperCase() || word === word.toLowerCase();
          if (isUniformCase) {
            return capitalizeFirst(word.toLowerCase());
          }
          return word;
        }
        return word.toLowerCase();
      })
      .join(" ");
  };

  export const formatBrandAndProductName = (
    brandAndProductName: BrandAndProductName,
  ): BrandAndProductName => {
    let { brandName, productName } = brandAndProductName;

    // Get all brand parts by splitting on comma
    const brandParts = brandName
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p);

    // Select which brand to use
    let selectedBrand = must(brandParts[0], "Brand name must not be empty");

    if (brandParts.length > 1) {
      // Try to find a brand part that appears in the product (case-insensitive)
      const productLower = productName.toLowerCase();
      let foundBrand: string | undefined;
      for (const part of brandParts) {
        if (productLower.includes(part.toLowerCase())) {
          foundBrand = part;
          break;
        }
      }

      // If no part appears in product, prefer ones without de-prioritized substrings
      if (!foundBrand) {
        const candidatesWithoutSuffixes = brandParts.filter((part) => {
          return !dePrioritizeBrandSubstrings.some((suffix) => part.includes(suffix));
        });
        selectedBrand = must(
          candidatesWithoutSuffixes[0] || brandParts[0],
          "Brand name must not be empty",
        );
      } else {
        selectedBrand = foundBrand;
      }
    }

    // Normalize whitespace and check if product is a substring of brand
    selectedBrand = selectedBrand.replace(/\s+/g, " ");
    const selectedBrandLower = selectedBrand.toLowerCase();
    const productLower = productName.toLowerCase();
    if (selectedBrandLower.includes(productLower)) {
      // Product is part of brand, swap them
      const tempBrand = selectedBrand;
      selectedBrand = productName;
      productName = must(
        tempBrand.replace(new RegExp(productName, "i"), "").trim(),
        "Product name must not be empty",
      );
    } else {
      // Remove brand from product if product starts with brand
      if (productLower.startsWith(selectedBrandLower)) {
        let remainder = productName.slice(selectedBrand.length).trim();
        if (remainder.startsWith(",")) {
          remainder = remainder.slice(1).trim();
        }
        if (remainder) {
          productName = remainder;
        }
      }
    }

    // Clean up product name: remove special markers
    productName = productName
      .replace(/®\s*brand\s+/i, "")
      .replace(/®\s*/g, "")
      .trim();

    // Apply title case to both brand and product names
    selectedBrand = toTitleCase(selectedBrand);
    productName = toTitleCase(productName);

    return { brandName: selectedBrand, productName };
  };
}

namespace OpenFoodFactsProduct {
  const ignoreBrands = ["", "N/A", "Restaurant Item"];
  const ignoreQualityTags = [
    "en:environmental",
    "en:vegetarian",
    "en:vegan",
    "en:ingredients",
    "en:ecoscore",
  ];

  const servingQuantityUnitSchema = z.enum([
    "kg",
    "g",
    "mg",
    "µg",
    "oz",
    "l",
    "ml",
    "fl.oz",
    "fl oz",
  ]);

  export const schema = z.object({
    _id: z.string(),
    code: z.string(),
    rev: z.number(),
    brands: z.string().refine((value) => !ignoreBrands.includes(value.trim())),
    product_name_en: z.string(),
    product_name: z.string().optional(),
    product_name_fr: z.string().optional(),
    categories_tags: z.array(z.string()).optional().default([]),
    countries_tags: z.array(z.string()).refine((value) => value.includes("en:united-states")),
    serving_size: z.string(),
    serving_size_imported: z.string().optional(),
    serving_quantity: z.coerce.number(),
    serving_quantity_unit: servingQuantityUnitSchema,
    nutriments: z.record(z.string(), z.number().or(z.string())),
    data_quality_warnings_tags: z
      .string()
      .array()
      .optional()
      .transform((value) =>
        (value || []).filter((e) => ignoreQualityTags.every((tag) => !e.startsWith(tag))),
      ),
    data_quality_bugs_tags: z
      .string()
      .array()
      .optional()
      .transform((value) =>
        (value || []).filter((e) => ignoreQualityTags.every((tag) => !e.startsWith(tag))),
      ),
    data_quality_errors_tags: z.string().array().optional(),
    quantity: z.string().optional(),
    product_quantity: z.coerce.number().optional(),
    product_quantity_unit: servingQuantityUnitSchema.optional(),
  });

  export type Input = z.infer<typeof schema>;
  type ServingQuantityUnit = z.infer<typeof servingQuantityUnitSchema>;

  export type NutrientRef = {
    id: string;
    key: Nutrient.Key | null;
    unitKey: Unit.Key;
  };

  export type Context = {
    servingParser: ServingParser.Value;
    openfoodfactsCollectionId: string;
    openfoodfactsNutrients: Map<string, NutrientRef>;
    ignoreNutrients: Set<string>;
    keepRestrictedBarcodesAsNull?: boolean;
  };

  // export type BrandedFoodCreateInput = {
  //   id: string;
  //   modifiers: [];
  //   brandedFood: {
  //     code: Barcode.Canonical | null;
  //     brandName: string;
  //     productName: string;
  //   };
  //   serving: {
  //     id: string;
  //     sortIndex: "0";
  //     nutrients: { nutrientId: string; amount: number }[];
  //     name: string;
  //     defaultAmount: number;
  //     modifiers: Food.ServingModifier[];
  //     mass: { unitKey: Unit.Key<"mass">; quantity: number } | null;
  //     volume: { unitKey: Unit.Key<"volume">; quantity: number } | null;
  //   };
  //   external: {
  //     id: string;
  //     version: string;
  //     importVersion: number;
  //     name: string;
  //     variant?: string | null;
  //   };
  //   collectionId: string;
  //   visibility: "public";
  //   discoverability: "listed";
  // };

  const NUTRIENT_IGNORE_LIST = new Set([
    "nutrition-score-fr",
    "nova-group",
    "fruits-vegetables-nuts-estimate-from-ingredients",
    "fruits-vegetables-legumes-estimate-from-ingredients",
    "carbon-footprint-from-known-ingredients",
    "en-panthothenic",
    "energy-kj",
    "phylloquinone",
    "0",
    "energy-cal",
    "fibra-dietetica",
    "en-fibra-dietetica",
    "en-vatimin-a",
    "en-lycopene",
    "en-selenum",
    "energy-g",
    "melisse",
    "en-boron",
    "en-vanadium",
    "en-silicon",
    "en-lutein",
    "en-vegetable-glycerin",
  ]);

  const requiredNutrientKeys: Nutrient.Key[] = ["energy", "protein", "carbohydrate", "fat"];

  const unitTranslations: Record<ServingQuantityUnit, Unit.Key<"volume" | "mass">> = {
    g: "g",
    mg: "mg",
    kg: "kg",
    oz: "oz",
    µg: "mcg",
    ml: "mL",
    "fl.oz": "fl_oz_us",
    "fl oz": "fl_oz_us",
    l: "L",
  };

  const skippedServingUnitKeys = new Set<Unit.Key<"mass" | "volume">>(["mg", "pt_us", "L"]);
  const servingQuantityRelativeTolerance = 0.15;

  export function parse(raw: unknown): Input | null {
    const result = schema.safeParse(raw);
    if (!result.success) {
      return null;
    }
    if (result.data.data_quality_errors_tags && result.data.data_quality_errors_tags.length > 0) {
      return null;
    }
    return result.data;
  }

  export async function toBrandedFoodCreateInput(
    data: Input,
    context: Context,
  ): Promise<BrandedFood.Create | null> {
    const serving = toServing(data, context);
    if (!serving) {
      return null;
    }

    const { brandName, productName } = OpenFoodFactsImporter.formatBrandAndProductName({
      brandName: data.brands,
      productName: data.product_name_en,
    });

    const canonicalCode = Barcode.canonicalizeDigits(data.code);
    if (!context.keepRestrictedBarcodesAsNull && !canonicalCode) {
      return null;
    }

    return {
      id: createId(),
      modifiers: [],
      brandedFood: {
        code: canonicalCode,
        brandName,
        productName,
      },
      serving,
      external: {
        id: data._id,
        version: data.rev.toString(),
        importVersion: 1,
        name: data.product_name_en,
        variant: null,
      },
      collectionId: context.openfoodfactsCollectionId,
      visibility: "public",
      discoverability: "listed",
    };
  }

  function toServing(data: Input, context: Context): BrandedFood.Create["serving"] | null {
    const servingSizeParseResult = context.servingParser.parse(data.serving_size);
    if (servingSizeParseResult) {
      if (!isPositiveFinite(servingSizeParseResult.defaultAmount)) {
        return null;
      }

      if (servingSizeParseResult.mass || servingSizeParseResult.volume) {
        if (
          (servingSizeParseResult.mass &&
            isSkippedServingUnitKey(servingSizeParseResult.mass.unitKey)) ||
          (servingSizeParseResult.volume &&
            isSkippedServingUnitKey(servingSizeParseResult.volume.unitKey))
        ) {
          return null;
        }

        if (hasContradictoryServingQuantity(data, servingSizeParseResult)) {
          return null;
        }

        const nutrients = nutrientAmountsFromOpenFoodFactsNutriments(
          data,
          context,
          1 / servingSizeParseResult.defaultAmount,
        );
        if (!nutrients) {
          return null;
        }
        const servingModifiers = servingSizeParseResult.modifiers as Food.ServingModifier[];
        return {
          id: createId(),
          sortIndex: "0",
          nutrients,
          name: servingSizeParseResult.name,
          defaultAmount: servingSizeParseResult.defaultAmount,
          modifiers: servingModifiers,
          mass: servingSizeParseResult.mass,
          volume: servingSizeParseResult.volume,
        };
      }

      if (!isPositiveFinite(data.serving_quantity)) {
        return null;
      }
    }

    const massUnitKey = castServingQuantityUnitToMassUnitKey(data.serving_quantity_unit);
    const volumeUnitKey = castServingQuantityUnitToVolumeUnitKey(data.serving_quantity_unit);

    let mass: BrandedFood.Create["serving"]["mass"] = null;
    if (massUnitKey) {
      mass = {
        unitKey: massUnitKey,
        quantity: data.serving_quantity,
      };
    }

    let volume: BrandedFood.Create["serving"]["volume"] = null;
    if (volumeUnitKey) {
      volume = {
        unitKey: volumeUnitKey,
        quantity: data.serving_quantity,
      };
    }

    if (!mass && !volume) {
      return null;
    }

    if (
      (mass && isSkippedServingUnitKey(mass.unitKey)) ||
      (volume && isSkippedServingUnitKey(volume.unitKey))
    ) {
      return null;
    }

    const defaultAmount = data.serving_quantity;
    if (!isPositiveFinite(defaultAmount)) {
      return null;
    }

    const nutrients = nutrientAmountsFromOpenFoodFactsNutriments(data, context, 1 / defaultAmount);
    if (!nutrients) {
      return null;
    }

    return {
      id: createId(),
      sortIndex: "0",
      name: data.serving_size,
      defaultAmount,
      modifiers: [],
      mass,
      volume,
      nutrients,
    };
  }

  function isPositiveFinite(value: number): boolean {
    return Number.isFinite(value) && value > 0;
  }

  function isSkippedServingUnitKey(unitKey: Unit.Key<"mass" | "volume">): boolean {
    return skippedServingUnitKeys.has(unitKey);
  }

  function hasContradictoryServingQuantity(
    data: Input,
    parsedServing: ServingParser.Result,
  ): boolean {
    if (!isPositiveFinite(data.serving_quantity)) {
      return true;
    }

    const massUnitKey = castServingQuantityUnitToMassUnitKey(data.serving_quantity_unit);
    if (massUnitKey) {
      if (!parsedServing.mass) {
        return true;
      }

      const parsedMassQuantity = parsedServing.mass.quantity * parsedServing.defaultAmount;
      if (!isPositiveFinite(parsedMassQuantity)) {
        return true;
      }

      const normalizedServingQuantity = Unit.convert(
        {
          quantity: data.serving_quantity,
          unitKey: massUnitKey,
        },
        parsedServing.mass.unitKey,
      ).quantity;

      return !isWithinRelativeTolerance(parsedMassQuantity, normalizedServingQuantity);
    }

    const volumeUnitKey = castServingQuantityUnitToVolumeUnitKey(data.serving_quantity_unit);
    if (volumeUnitKey) {
      if (!parsedServing.volume) {
        return true;
      }

      const parsedVolumeQuantity = parsedServing.volume.quantity * parsedServing.defaultAmount;
      if (!isPositiveFinite(parsedVolumeQuantity)) {
        return true;
      }

      const normalizedServingQuantity = Unit.convert(
        {
          quantity: data.serving_quantity,
          unitKey: volumeUnitKey,
        },
        parsedServing.volume.unitKey,
      ).quantity;

      return !isWithinRelativeTolerance(parsedVolumeQuantity, normalizedServingQuantity);
    }

    return true;
  }

  function isWithinRelativeTolerance(left: number, right: number): boolean {
    const relativeDifference = Math.abs(left - right) / Math.max(Math.abs(right), Number.EPSILON);
    return relativeDifference <= servingQuantityRelativeTolerance;
  }

  function castServingQuantityUnitToMassUnitKey(unit: ServingQuantityUnit) {
    const correctedUnit = unitTranslations[unit] ?? unit;
    return Unit.keySchema(["mass"]).safeParse(correctedUnit).data;
  }

  function castServingQuantityUnitToVolumeUnitKey(unit: ServingQuantityUnit) {
    const correctedUnit = unitTranslations[unit] ?? unit;
    return Unit.keySchema(["volume"]).safeParse(correctedUnit).data;
  }

  function nutrientAmountsFromOpenFoodFactsNutriments(
    data: Input,
    context: Context,
    scaleFactor: number,
  ): BrandedFood.Create["serving"]["nutrients"] | null {
    const seenNutrientIds = new Set<string>();
    const seenNutrientKeys = new Set<string>();
    const result: BrandedFood.Create["serving"]["nutrients"] = [];

    for (const [key, value] of Object.entries(data.nutriments)) {
      const [maybeId, suffix] = key.split("_");
      const openfoodfactsNutrientId = must(maybeId);
      if (
        NUTRIENT_IGNORE_LIST.has(openfoodfactsNutrientId) ||
        context.ignoreNutrients.has(openfoodfactsNutrientId)
      ) {
        continue;
      }

      const nutrient = context.openfoodfactsNutrients.get(openfoodfactsNutrientId);
      if (!nutrient || suffix !== "serving" || typeof value !== "number") {
        continue;
      }

      if (seenNutrientIds.has(nutrient.id)) {
        continue;
      }
      seenNutrientIds.add(nutrient.id);
      if (nutrient.key) {
        seenNutrientKeys.add(nutrient.key);
      }

      let amount: number;
      if (nutrient.unitKey === "kcal" || nutrient.unitKey === "%" || nutrient.unitKey === "count") {
        amount = value;
      } else {
        amount = Unit.convert({ quantity: value, unitKey: "g" }, nutrient.unitKey).quantity;
      }

      result.push({
        nutrientId: nutrient.id,
        amount: amount * scaleFactor,
      });
    }

    const hasMissingRequiredNutrients = requiredNutrientKeys.some(
      (key) => !seenNutrientKeys.has(key),
    );
    if (hasMissingRequiredNutrients) {
      return null;
    }

    return result;
  }
}

namespace OpenFoodFactsCategoryGraph {
  export type Snapshot = OpenFoodFactsTaxonomy.Snapshot;
  export type Resolver = OpenFoodFactsTaxonomy.Resolver;

  export function createResolver(snapshot: Snapshot): Resolver {
    return OpenFoodFactsTaxonomy.createResolver(snapshot);
  }
}

namespace OpenFoodFactsCategoryResolution {
  export type Resolver = OpenFoodFactsCategoryGraph.Resolver;

  export type ResolutionDetails = {
    inputTags: string[];
    effectiveTags: string[];
    ignoredTags: string[];
    unmatchedTags: string[];
    highRiskOverrideTags: string[];
    directCategoryKeys: string[];
    inferredPreferredCategoryKeys: string[];
    fallbackCategoryKeys: string[];
    blockedFallbacks: {
      tag: string;
      categoryKey: string;
      blockedByCategoryKeys: string[];
    }[];
    mixedCandidates: {
      tag: string;
      preferredCategoryKeys: string[];
      fallbackCategoryKey: string | null;
      requiresSupport: string[];
    }[];
    baseOrderedCategoryKeys: string[];
    finalCategoryKey: string | null;
    finalOrderedCategoryKeys: string[];
    strategy: CategoryDecisionTrace["strategy"];
    usedFallback: boolean;
    reasons: string[];
  };

  const sourcePolicy = FoodCategoryCrosswalkPolicy.sourceLensPolicies.offTopUsTagsV1;

  function unique(values: readonly string[]) {
    return [...new Set(values)];
  }

  function sourceIdsFromTags(categoryTags: readonly string[]) {
    const sourceIds: string[] = [];
    const sourceLabelById = new Map<string, string>();

    for (const tag of categoryTags) {
      const normalizedTag = normalizeOffCategorySourceId(tag);
      if (normalizedTag) {
        sourceIds.push(normalizedTag);
        sourceLabelById.set(normalizedTag, tag.trim() || normalizedTag);
      }
    }

    return { sourceIds: unique(sourceIds), sourceLabelById };
  }

  function offTagFromSourceId(sourceId: string) {
    const prefix = "off:category:";
    return sourceId.startsWith(prefix) ? sourceId.slice(prefix.length) : sourceId;
  }

  export function resolveCategoryResolutionDetails(args: {
    categoryTags: readonly string[];
    productName?: string | null;
    categoryGraph?: Resolver | null;
  }): ResolutionDetails {
    const { sourceIds, sourceLabelById } = sourceIdsFromTags(args.categoryTags);
    const normalizedInput = normalizedSourceIdMap(args.categoryTags, normalizeOffCategorySourceId);

    for (const [sourceId, label] of normalizedInput.sourceLabelById) {
      sourceLabelById.set(sourceId, label);
    }

    const tagForSourceId = (sourceId: string) => sourceLabelById.get(sourceId) ?? sourceId;
    const evaluation = evaluateFoodCategoryPolicyDetails({
      sourceIds,
      ignoreSourceIds: sourcePolicy.ignoreSourceIds,
      highRiskOverrideSourceIds: sourcePolicy.highRiskOverrideSourceIds,
      rules: sourcePolicy.rules,
      textInputs: [args.productName],
      textRefinementRules: sourcePolicy.textRefinementRules,
      graph: args.categoryGraph
        ? {
            getAncestorSourceIds(sourceId) {
              const tag = offTagFromSourceId(sourceId);
              return (
                args.categoryGraph?.getAncestorTags(tag).flatMap((ancestorTag) => {
                  const ancestorSourceId = normalizeOffCategorySourceId(ancestorTag);
                  if (!ancestorSourceId) {
                    return [];
                  }

                  if (!sourceLabelById.has(ancestorSourceId)) {
                    sourceLabelById.set(ancestorSourceId, ancestorTag);
                  }

                  return [ancestorSourceId];
                }) ?? []
              );
            },
          }
        : undefined,
    });
    const effectiveTags = evaluation.inputSourceIds.map(tagForSourceId);

    let strategy: CategoryDecisionTrace["strategy"] = "unresolved";
    let finalCategoryKey: string | null = null;
    const reasons: string[] = [];

    if (evaluation.textRefinement) {
      strategy = "override";
      finalCategoryKey = evaluation.textRefinement.categoryKey;
      reasons.push(evaluation.textRefinement.reason);
    } else if (evaluation.directCategoryKeys.length > 0) {
      strategy = "direct";
      finalCategoryKey = evaluation.directCategoryKeys[0] ?? null;
      reasons.push("OFF source policy found direct category match");
    } else if (evaluation.inferredPreferredCategoryKeys.length > 0) {
      strategy = "preferred";
      finalCategoryKey = evaluation.inferredPreferredCategoryKeys[0] ?? null;
      reasons.push("OFF source policy inferred preferred category");
    } else if (evaluation.fallbackCategoryKeys.length > 0) {
      strategy = "fallback";
      finalCategoryKey = evaluation.fallbackCategoryKeys[0] ?? null;
      reasons.push("OFF source policy used fallback category");
    } else {
      reasons.push("no direct, preferred, or fallback category found");
    }

    return {
      inputTags: unique(normalizedInput.sourceIds).map(tagForSourceId),
      effectiveTags,
      ignoredTags: evaluation.ignoredSourceIds.map(tagForSourceId),
      unmatchedTags: evaluation.unmatchedSourceIds.map(tagForSourceId),
      highRiskOverrideTags: evaluation.highRiskOverrideSourceIds.map(tagForSourceId),
      directCategoryKeys: evaluation.directCategoryKeys,
      inferredPreferredCategoryKeys: evaluation.inferredPreferredCategoryKeys,
      fallbackCategoryKeys: evaluation.fallbackCategoryKeys,
      blockedFallbacks: evaluation.blockedFallbacks.map((fallback) => ({
        tag: tagForSourceId(fallback.sourceId),
        categoryKey: fallback.categoryKey,
        blockedByCategoryKeys: fallback.blockedByCategoryKeys,
      })),
      mixedCandidates: evaluation.mixedCandidates.map((candidate) => ({
        tag: tagForSourceId(candidate.sourceId),
        preferredCategoryKeys: candidate.preferredCategoryKeys,
        fallbackCategoryKey: candidate.fallbackCategoryKey,
        requiresSupport: candidate.requiresSupport,
      })),
      baseOrderedCategoryKeys: evaluation.orderedCategoryKeys,
      finalCategoryKey,
      finalOrderedCategoryKeys: evaluation.orderedCategoryKeys,
      strategy,
      usedFallback: strategy === "fallback",
      reasons,
    };
  }

  export function resolveCategoryKeys(args: {
    categoryTags: readonly string[];
    productName?: string | null;
    categoryGraph?: Resolver | null;
  }): string[] {
    return resolveCategoryResolutionDetails(args).finalOrderedCategoryKeys;
  }
}

function rawOpenFoodFactsProductName(rawProduct: unknown): string | null {
  const result = z
    .object({
      product_name_en: z.string().optional(),
      product_name: z.string().optional(),
      product_name_fr: z.string().optional(),
    })
    .safeParse(rawProduct);
  if (!result.success) {
    return null;
  }

  for (const candidate of [
    result.data.product_name_en,
    result.data.product_name,
    result.data.product_name_fr,
  ]) {
    const trimmed = candidate?.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  return null;
}

namespace OpenFoodFactsImport {
  export type ResolveBrandedFoodProductResult =
    | {
        ok: true;
        args: BrandedFood.Create;
        offCategoryTags: string[];
        offProductName: string | null;
        categoryKeys: string[];
        categoryResolution?: OpenFoodFactsCategoryResolution.ResolutionDetails;
      }
    | {
        ok: false;
        parsedProduct: OpenFoodFactsProduct.Input | null;
      };

  export async function resolveBrandedFoodProduct(args: {
    rawProduct: unknown;
    context: OpenFoodFactsProduct.Context;
    categoryGraph?: OpenFoodFactsCategoryGraph.Resolver | null;
    includeCategoryResolution?: boolean;
  }): Promise<ResolveBrandedFoodProductResult> {
    const parsedProduct = OpenFoodFactsProduct.parse(args.rawProduct);
    if (!parsedProduct) {
      return { ok: false, parsedProduct: null };
    }

    const transformedInput = await OpenFoodFactsProduct.toBrandedFoodCreateInput(
      parsedProduct,
      args.context,
    );
    if (!transformedInput) {
      return { ok: false, parsedProduct };
    }

    const offCategoryTags = parsedProduct.categories_tags ?? [];
    const offProductName = rawOpenFoodFactsProductName(args.rawProduct);
    const categoryKeys = OpenFoodFacts.resolveCategoryKeys({
      categoryTags: offCategoryTags,
      productName: offProductName,
      categoryGraph: args.categoryGraph ?? null,
    });

    return {
      ok: true,
      args: transformedInput,
      offCategoryTags,
      offProductName,
      categoryKeys,
      ...(args.includeCategoryResolution
        ? {
            categoryResolution: OpenFoodFacts.resolveCategoryResolutionDetails({
              categoryTags: offCategoryTags,
              productName: offProductName,
              categoryGraph: args.categoryGraph ?? null,
            }),
          }
        : {}),
    };
  }
}

export namespace OpenFoodFacts {
  export const Importer = OpenFoodFactsImporter;
  export const Product = OpenFoodFactsProduct;
  export const CategoryGraph = OpenFoodFactsCategoryGraph;
  export const CategoryResolution = OpenFoodFactsCategoryResolution;
  export const Import = OpenFoodFactsImport;
  export namespace Product {
    export type Input = OpenFoodFactsProduct.Input;
    export type NutrientRef = OpenFoodFactsProduct.NutrientRef;
    export type Context = OpenFoodFactsProduct.Context;
  }
  export namespace CategoryGraph {
    export type Snapshot = OpenFoodFactsCategoryGraph.Snapshot;
    export type Resolver = OpenFoodFactsCategoryGraph.Resolver;
  }
  export namespace CategoryResolution {
    export type Resolver = OpenFoodFactsCategoryResolution.Resolver;
    export type ResolutionDetails = OpenFoodFactsCategoryResolution.ResolutionDetails;
  }
  export namespace Import {
    export type ResolveBrandedFoodProductResult =
      OpenFoodFactsImport.ResolveBrandedFoodProductResult;
  }
  export const slug = "openfoodfacts";

  export const resolveCategoryKeys = OpenFoodFactsCategoryResolution.resolveCategoryKeys;
  export const resolveCategoryResolutionDetails =
    OpenFoodFactsCategoryResolution.resolveCategoryResolutionDetails;

  export const barcodeLookupFailureReasons = [
    "not_found",
    "rejected_non_us",
    "rejected_restricted_code",
    "rejected_incomplete_nutrition",
    "upstream_timeout",
    "upstream_error",
  ] as const;

  export type BarcodeLookupFailureReason = (typeof barcodeLookupFailureReasons)[number];
  type OpenFoodFactsLookupResult = {
    product: unknown;
    reason: "not_found" | "upstream_timeout" | "upstream_error";
  };
  type OpenFoodFactsLookup = (code: string) => Promise<OpenFoodFactsLookupResult>;

  export type ResolveBrandedFoodByBarcodeResult =
    | {
        ok: true;
        args: BrandedFood.Create;
        offCategoryTags: string[];
        offProductName: string | null;
        categoryKeys: string[];
        categoryResolution?: CategoryResolution.ResolutionDetails;
      }
    | {
        ok: false;
        reason: BarcodeLookupFailureReason;
      };

  const requiredServingNutrimentKeys = [
    "energy-kcal_serving",
    "proteins_serving",
    "carbohydrates_serving",
    "fat_serving",
  ] as const;

  const countriesTagsProbeSchema = z.object({
    countries_tags: z.array(z.string()),
  });

  const nutrimentsProbeSchema = z.object({
    nutriments: z.record(z.string(), z.unknown()),
  });

  const upstreamLookupBodySchema = z.object({
    product: z.unknown().optional(),
  });

  function hasUnitedStatesCountryTag(rawProduct: unknown): boolean | null {
    const result = countriesTagsProbeSchema.safeParse(rawProduct);
    if (!result.success) {
      return null;
    }

    return result.data.countries_tags.includes("en:united-states");
  }

  function hasRequiredServingNutriments(rawProduct: unknown): boolean | null {
    const result = nutrimentsProbeSchema.safeParse(rawProduct);
    if (!result.success) {
      return null;
    }

    const typedNutriments = result.data.nutriments;
    return requiredServingNutrimentKeys.every((key) => {
      const value = typedNutriments[key];
      return typeof value === "number" && Number.isFinite(value);
    });
  }

  function classifyTransformFailure(
    parsedProduct: Product.Input,
  ): Exclude<BarcodeLookupFailureReason, "upstream_timeout" | "upstream_error"> {
    const canonicalCode = Barcode.canonicalizeDigits(parsedProduct.code);
    if (!canonicalCode) {
      return "rejected_restricted_code";
    }

    const hasRequiredNutriments = hasRequiredServingNutriments(parsedProduct);
    if (hasRequiredNutriments === false) {
      return "rejected_incomplete_nutrition";
    }

    return "not_found";
  }

  async function fetchProductFromUpstream(code: string): Promise<OpenFoodFactsLookupResult> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), OFF_LOOKUP_TIMEOUT_MS);

    try {
      const url = `${OFF_UPSTREAM_BASE_URL}/api/v2/product/${encodeURIComponent(code)}.json?fields=${OFF_LOOKUP_FIELDS}`;
      const response = await fetch(url, {
        headers: { "User-Agent": OFF_USER_AGENT },
        signal: controller.signal,
      });
      if (!response.ok) {
        return {
          product: null,
          reason: response.status === 404 ? "not_found" : "upstream_error",
        };
      }

      const body = upstreamLookupBodySchema.safeParse(await response.json());
      return {
        product: body.success ? (body.data.product ?? null) : null,
        reason: "not_found",
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return { product: null, reason: "upstream_timeout" };
      }
      return { product: null, reason: "upstream_error" };
    } finally {
      clearTimeout(timeout);
    }
  }

  export async function resolveBrandedFoodByBarcode({
    code,
    offBarcodeLookup,
    openfoodfactsCollectionId,
    servingParser,
    openfoodfactsNutrients,
    ignoreNutrients,
    categoryGraph,
  }: {
    code: Barcode.Canonical;
    offBarcodeLookup?: OpenFoodFactsLookup;
    openfoodfactsCollectionId: string;
    servingParser: ServingParser.Value;
    openfoodfactsNutrients: Map<string, Product.NutrientRef>;
    ignoreNutrients?: Set<string>;
    categoryGraph?: CategoryGraph.Resolver | null;
  }): Promise<ResolveBrandedFoodByBarcodeResult> {
    const lookupResult = await (offBarcodeLookup ?? fetchProductFromUpstream)(code);
    const rawProduct = lookupResult.product;
    if (!rawProduct) {
      return { ok: false, reason: lookupResult.reason };
    }

    const isUnitedStatesProduct = hasUnitedStatesCountryTag(rawProduct);
    if (isUnitedStatesProduct === false) {
      return { ok: false, reason: "rejected_non_us" };
    }

    const hasRequiredNutriments = hasRequiredServingNutriments(rawProduct);
    if (hasRequiredNutriments === false) {
      return { ok: false, reason: "rejected_incomplete_nutrition" };
    }

    const importResult = await OpenFoodFactsImport.resolveBrandedFoodProduct({
      rawProduct,
      context: {
        servingParser,
        openfoodfactsCollectionId,
        openfoodfactsNutrients,
        ignoreNutrients: ignoreNutrients ?? new Set(),
      },
      categoryGraph,
    });
    if (!importResult.ok) {
      if (importResult.parsedProduct) {
        return { ok: false, reason: classifyTransformFailure(importResult.parsedProduct) };
      }
      return { ok: false, reason: "not_found" };
    }

    return {
      ok: true,
      args: importResult.args,
      offCategoryTags: importResult.offCategoryTags,
      offProductName: importResult.offProductName,
      categoryKeys: importResult.categoryKeys,
      ...(importResult.categoryResolution
        ? { categoryResolution: importResult.categoryResolution }
        : {}),
    };
  }
}
